import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { getAllSlotegratorGames, getSlotegratorGameCall, getSlotegratorGameTypes, getSlotegratorProviders, searchGame } from "../../helpers/repository";
import Banners from "../../components/Common/Banners";
import GameLaunchModal from "../../components/Common/GameLaunchModal";
import ButtonLoader from "../../components/Common/ButtonLoader";
import MessageBox from "../../components/Common/MessageBox";
import { SERVER_URL } from "../../helpers/constants";
import { GameSkeletonLoading, MobileGameSkeletonLoading } from "../../components/Common/GameSkeletonLoading";
import { Search, X } from "lucide-react";

const Bigwins = ({ showMinimal, isLiveGame, onGameClick }) => {
  const [games, setGames] = useState([]);
  const [gamesData, setGamesData] = useState([])
  const [filteredGames, setFilteredGames] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [iframeURL, setIframeURL] = useState(null);
  const [gameName, setGameName] = useState("");
  const [fetchingGame, setFetchingGame] = useState(false);
  const [providerNames, setProviderNames] = useState({});
  const [buttonLoader, setButtonLoader] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("Loading Data...");
  const [showSearch, setShowSearch] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")
  const history = useHistory();
  const Banner = useSelector((state) =>
    state?.Banner?.Index.filter((banner) => banner["page_name"] === "Home Page")
  );

  // First useEffect to fetch game categories and providers
  useEffect(() => {
    const fetchData = async () => {
      setButtonLoader(true);
      try {
        const [gameTypesResponse, providerNamesResponse] = await Promise.all([
          getSlotegratorGameTypes(),
          getSlotegratorProviders(),
        ]);

        const gameCategories = [
          { id: "All", name: "All" },
          ...gameTypesResponse?.data?.data,
        ];
        setGames(gameCategories);

        const names = providerNamesResponse.data.data.reduce(
          (acc, provider) => {
            acc[provider.id] = provider.name;
            return acc;
          },
          {}
        );
        setProviderNames(names);
        // AllGames is loaded by the selectedCategory effect on mount
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setButtonLoader(false);
      }
    };

    fetchData();
  }, []);

  // Second useEffect to fetch games based on selected category (runs on mount too)
  useEffect(() => {
    const fetchGames = async () => {
      setButtonLoader(true);
      try {
        const response = await getAllSlotegratorGames(
          selectedCategory === "All" ? undefined : selectedCategory,
        );
        setFilteredGames(response?.data?.data || []);
        setGamesData(response?.data?.data || []);
        setLoadingMessage(null);
      } catch (error) {
        console.error("Error fetching games:", error);
      } finally {
        setButtonLoader(false);
      }
    };

    fetchGames();
  }, [selectedCategory]);

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
  };

  const groupedGames = useMemo(() => {
    return filteredGames.reduce((acc, game) => {
      const providerId = game.provider_id || "Unknown Provider";
      if (!acc[providerId]) {
        acc[providerId] = [];
      }
      acc[providerId].push(game);
      return acc;
    }, {});
  }, [filteredGames]);

  // const groupedGames = groupGamesByProvider();

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.trim() === "") {
      setFilteredGames(gamesData);
    }
  };

  const handleSeeAllClick = (providerId) => {
    history.push(`/provider/${providerId}`);
  };


  const handleSearchGameFromAllProvider = async () => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) {
      setFilteredGames(gamesData);
      setLoadingMessage(null);
      return;
    }

    setButtonLoader(true);
    setLoadingMessage("Searching for games...");

    try {
      let results = [];

      if (selectedCategory !== "All") {
        const response = await searchGame(selectedCategory, query);
        results = response?.data?.data || [];
      } else {
        results = gamesData.filter((game) =>
          game.name.toLowerCase().includes(query)
        );
      }
      if (results.length > 0) {
        setFilteredGames(results);
        setLoadingMessage(null);
      } else {
        setFilteredGames([]);
        setLoadingMessage("No games found matching your search.");
      }
    } catch (error) {
      console.error("Error fetching searched games:", error);
    } finally {
      setButtonLoader(false);
      setShowSearch(false)
    }
  };




  const handleClearSearch = async () => {
    setButtonLoader(true);
    try {
      const response = await getAllSlotegratorGames();
      setFilteredGames(response?.data?.data || []);
      setShowSearch(false);
    } catch (error) {
      console.error("Error fetching all games:", error);
    } finally {
      setButtonLoader(false);
    }
  }


  const customStyles = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "67%",
      overflow: "hidden",
      padding: "20px",
    },
  };

  // When a parent (e.g. GamesHub) supplies its own game-launch handler, delegate to it
  // instead of managing a second, duplicate GameLaunchModal instance here.
  const handleGameClick = (game, name = "") => {
    if (onGameClick) {
      onGameClick(game, name);
      return;
    }
    setGameName(name);
    setFetchingGame(true);
    getSlotegratorGameCall(game, successGetGameUrl, errorGetGameUrl)?.catch?.(
      errorGetGameUrl
    );
  };

  const successGetGameUrl = (res) => {
    setFetchingGame(false);
    setIframeURL(decodeURIComponent(res));
  };

  const errorGetGameUrl = (error) => {
    setFetchingGame(false);
    console.error("Error fetching game URL:", error);
  };


  return (
    <div
      className={`${showMinimal === true
        ? "bg-[var(--bet-fore-mutted)] p-2 rounded-md shadow-sm "
        : ""
        }`}
    >
      <div className="hidden md:flex">
        {buttonLoader ? (
          <GameSkeletonLoading isLiveGame={isLiveGame ?? false} />
        ) : (
          <div className={isLiveGame ? "w-full flex" : "w-full gap-3 px-3 flex"}>
            {!isLiveGame && (
              <>
                <div className="flex flex-col bg-[#1a2235] max-h-fit pb-10 min-w-[160px] border-r border-gray-800 min-h-screen sticky top-0">
                  {games.map((game) => (
                    <div
                      key={game.id}
                      onClick={() => handleCategoryClick(game.id)}
                      className={`flex items-center justify-between gap-2 py-3 px-2 capitalize cursor-pointer hover:bg-[#1e1e1e] ${selectedCategory === game.id
                        ? "bg-[#1e1e1e] border-l-4 border-[#FFED00]"
                        : ""
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        <img
                          src={`${SERVER_URL}/slotegrator/categories/${game.name}.png`}
                          onError={(e) => ((e.target).style.display = "none")}
                          alt={game.name}
                          className="w-6 h-6 object-contain"
                        />

                        <span className="text-white text-sm">{game.name}</span>

                      </div>

                      {game.name === "All" && (
                        <button
                          type="button"
                          onClick={() => setShowSearch(true)}
                          className="ml-auto text-gray-300 hover:text-white transition relative"
                        >
                          <Search size={20} />
                        </button>
                      )}

                      {game.name === "All" && (
                        <div
                          className={`absolute top-2 left-[100px] z-50 transform transition-all duration-500 ease-in-out ${showSearch
                            ? "opacity-100 translate-x-0 scale-100"
                            : "opacity-0 -translate-x-5 scale-95 pointer-events-none"
                            }`}
                        >
                          <div className="relative backdrop-blur-md bg-[#1b1c20]/95 border-2 border-transparent hover:border-[#FFED00] rounded-2xl transition-all duration-300 shadow-[0_0_25px_rgba(252,180,68,0.15)] hover:shadow-[0_0_40px_rgba(252,180,68,0.25)] overflow-hidden group">
                            <div className="flex items-center gap-2 px-2 py-1">
                              <>
                                <input
                                  id="sidebar-search"
                                  type="text"
                                  placeholder="Search games..."
                                  value={searchTerm}
                                  onChange={(e) => handleSearchChange(e)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="w-72 text-base font-semibold text-white bg-transparent px-2 py-2 pr-12 placeholder-gray-400 outline-none autofill:!bg-[#1b1c20] focus:ring-0 tracking-wide game-search-input"
                                />
                                <div className=" flex items-center justify-center">
                                  <X
                                    size={20}
                                    onClick={handleClearSearch}
                                    className="text-gray-400 group-hover:text-[#FFED00] transition-colors duration-300"
                                  />
                                </div>

                              </>
                              <button
                                onClick={() => {
                                  handleSearchGameFromAllProvider()
                                  searchGame(searchTerm)
                                }}
                                className="bg-gradient-to-r from-[#FFED00] to-[#f5c15d] text-[#1b1c20] font-bold px-4 py-[10px] !text-base !rounded-lg shadow-[0_0_10px_rgba(252,180,68,0.4)] hover:shadow-[0_0_20px_rgba(252,180,68,0.6)] transition-all duration-300 active:scale-95">Search</button>

                            </div>

                            <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-gradient-to-r from-[#FFED00] to-[#f5c15d] transition-all duration-500 group-hover:w-full"></div>
                          </div>
                        </div>
                      )}


                    </div>
                  ))}
                </div>
                <div className="overflow-y-auto max-h-full">
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 p-2 bg-[#1a2235] min-w-[calc(100%-170px)] items-start justify-start ">
                    {Object.values(groupedGames)
                      .flat()?.length ? Object.values(groupedGames)
                        .flat()
                        .map((game) => (
                          <div
                            key={game.id}
                            className="bg-[#1e2a3a] rounded-md cursor-pointer h-[140px] flex flex-col items-center justify-between p-2"
                            onClick={() => handleGameClick(game.uuid, game.name)}
                          >
                            <img
                              src={`${SERVER_URL}/slotegrator/slotegrator-games/${game.uuid}.png`}
                              alt={game.name}
                              onError={(e) => (e.target.style.display = "none")}
                              className="object-cover w-full h-[100px] rounded-sm"
                            />
                            <div className="text-white text-xs text-center truncate w-full">
                              {game.name}
                            </div>
                          </div>
                        )) : <NoSearchedGamesAvailable />}
                  </div>
                </div>

              </>
            )}

            {isLiveGame && (
              <div
                className="w-full bg-[var(--surface-1)] !border !border-[var(--border-subtle)] rounded-[var(--radius-card)]"
                style={{ padding: 12 }}
              >
                <div
                  style={{
                    font: "var(--type-label)", letterSpacing: "var(--ls-caps)", textTransform: "uppercase",
                    color: "var(--text-secondary)", marginBottom: 12,
                  }}
                >
                  Play Live Games
                </div>
                <div className="grid grid-cols-2 w-full" style={{ gap: 8 }}>
                  {Object.values(groupedGames)
                    .flat()
                    .slice(0, 4)
                    .map((game) => (
                      <div
                        key={game.id}
                        className="bg-[var(--surface-2)] !border !border-[var(--border-subtle)] rounded-[var(--radius-md)] cursor-pointer flex flex-col items-center justify-between w-full"
                        style={{ padding: 8 }}
                        onClick={() => handleGameClick(game.uuid, game.name)}
                      >
                        <img
                          src={`${SERVER_URL}/slotegrator/slotegrator-games/${game.uuid}.png`}
                          alt={game.name}
                          onError={(e) => (e.target.style.display = "none")}
                          className="object-cover w-full aspect-square rounded-[var(--radius-sm)]"
                        />
                        <div className="text-[var(--text-primary)] text-center truncate w-full" style={{ fontSize: 11, marginTop: 8 }}>
                          {game.name}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="block md:hidden bg-[#0c0d0e]">
        {buttonLoader ? (
          <div
            className="BLM-sportsHeader BLM-scrollableArea BLM-horizontal"
            style={{
              display: "flex",
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
          >
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                className="animate-pulse"
                style={{
                  padding: "10px",
                  minWidth: "100px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    backgroundColor: "#2e2e2e",
                    borderRadius: "10px",
                    marginBottom: "5px",
                  }}
                ></div>
                <div
                  style={{
                    width: "40px",
                    height: "10px",
                    backgroundColor: "#2e2e2e",
                    borderRadius: "5px",
                  }}
                ></div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className="BLM-sportsHeader BLM-scrollableArea BLM-horizontal"
            style={{
              display: "flex",
              overflowX: "auto",
              whiteSpace: "nowrap",
            }}
          >
            {games.map((game) => (
              <div
                key={game.id}
                onClick={() => handleCategoryClick(game.id)}
                className={`BLM-categoryListItem ${selectedCategory === game.id ? "selected" : ""
                  }`}
                style={{
                  padding: "10px",
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  color: selectedCategory === game.id ? "#FFED00" : "#fff",
                  display: "flex",
                  alignItems: "center",
                  borderBottom:
                    selectedCategory === game.id ? "4px solid #FFED00" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    maxWidth: "100px",
                  }}
                >
                  <img
                    src={`${SERVER_URL}/slotegrator/categories/${game.name}.png`}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                    style={{
                      width: "auto",
                      height: "auto",
                      maxWidth: "100px",
                      padding: "1px",
                    }}
                    alt={game.name}
                  />
                  <div>{game.name}</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="BLM-commonPage">
          <div className="w-full px-3 md:px-7 mb-6 flex items-center justify-center gap-3">

            {selectedCategory === "All" && <form
              onSubmit={(e) => {
                e.preventDefault()
                handleSearchGameFromAllProvider()
                searchGame(searchTerm)
              }}
              className="relative flex items-center w-full transition-all duration-300"
            >
              <div className="flex items-center w-full bg-[#1b1c20]/90 backdrop-blur-md rounded-2xl border border-transparent hover:border-[#FFED00] shadow-[0_0_20px_rgba(252,180,68,0.15)] hover:shadow-[0_0_35px_rgba(252,180,68,0.25)] transition-all duration-300">
                <div className="w-full flex items-center justify-between">
                  <input
                    type="text"
                    placeholder="Search for game name..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e)}
                    className="flex-1 bg-transparent text-white px-3 py-3 text-sm md:text-base placeholder-gray-400 outline-none focus:ring-0"
                  />

                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="p-3 bg-[#1b1c20]/90 rounded-2xl hover:border-[#FFED00] hover:shadow-[0_0_20px_rgba(252,180,68,0.25)] transition-all duration-300"
                    aria-label="Clear search"
                  >
                    <X size={20} className="text-[#FFED00]" />
                  </button>

                </div>
                <button
                  type="submit"
                  className="bg-[#FFED00] text-black px-4 py-3 font-semibold !rounded-r-2xl transition-all duration-300 hover:bg-[#f5c15d] focus:ring-2 focus:ring-[#FFED00]/60"
                  aria-label="Search"
                >
                  <Search size={18} />
                </button>
              </div>
            </form>}
          </div>
          <div className="BLM-commonPage-content">
            <div className="">
              {buttonLoader ? (
                <MobileGameSkeletonLoading />
              ) : (
                <div className="">
                  {![null, ""].includes(loadingMessage) ? (
                    <NoSearchedGamesAvailable />
                  ) : (
                    Object.keys(groupedGames).map((providerId) => (
                      <div key={providerId} className="mt-3">
                        <div
                          className="accordion-button-container"
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            backgroundColor: "#0c0d0e",
                            color: "white",
                            borderColor: "#00137f",
                            padding: "10px",
                          }}
                        >
                          <div className="accordion-button-text">
                            {providerNames[providerId] || providerId}
                          </div>
                          <button
                            onClick={() => handleSeeAllClick(providerId)}
                            style={{
                              color: "white",
                              border: "none",
                              background: "none",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            See All
                          </button>
                        </div>
                        <div
                          style={{
                            backgroundColor: "#0c0d0e",
                            padding: "20px",
                            cursor: "pointer",
                          }}
                        >
                          <div className="w-full flex gap-3 overflow-x-auto">
                            {groupedGames[providerId]
                              .slice(0, 6)
                              .map((game) => (
                                <div className="min-w-[160px]" key={game.id}>
                                  <img
                                    src={`${SERVER_URL}/slotegrator/slotegrator-games/${game.uuid}.png`}
                                    alt={game.name}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                    style={{
                                      objectFit: "cover",
                                      borderRadius: "10px",
                                    }}
                                    onClick={() => handleGameClick(game.uuid, game.name)}
                                  />
                                </div>
                              ))}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Game Modal — skipped when a parent supplies its own onGameClick/modal */}
      {!onGameClick && (
        <GameLaunchModal
          url={iframeURL}
          name={gameName}
          fetching={fetchingGame}
          onClose={() => {
            setIframeURL(null);
            setGameName("");
            setFetchingGame(false);
          }}
        />
      )}
    </div>
  );
};

export default Bigwins;

const NoSearchedGamesAvailable = () => {
  return (
    <div className="col-span-full md:min-w-[700px] w-full flex flex-col items-center justify-center py-24 text-center animate-fadeIn">
      <div className="bg-[#1e2a3a]/80 border border-[#FFED00]/40 rounded-xl p-6 max-w-md mx-auto shadow-[0_0_20px_rgba(252,180,68,0.1)]">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-12 h-12 text-[#FFED00] mx-auto mb-3"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={1.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M21 21l-4.35-4.35M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15z"
          />
        </svg>
        <p className="text-white text-lg font-semibold mb-1">
          No games found
        </p>
        <p className="text-gray-400 text-sm">
          We couldn’t find any games matching your search.
        </p>
      </div>
    </div>
  )
}

const GameSkeleton = ({ count = 24 }) => {
  return (
    <div className="hidden md:flex gap-3">
      <div className="w-[240px] bg-[#1b1b1c] py-4 px-2 border-r border-[#252526]">
        <div className="w-full animate-pulse space-y-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="h-6 w-full bg-gray-700 rounded px-2"></div>
          ))}
        </div>
      </div>

      <div className="relative min-w-[calc(100%-170px)] w-full flex flex-wrap bg-[#1a2235] justify-center items-center gap-4 p-2 pb-30">
        {Array.from({ length: count }).map((_, index) => (
          <div
            key={index}
            className="max-w-[180px] w-full self-stretch animate-pulse"
          >
            <div className="bg-gray-700 rounded-lg w-full h-[90px]"></div>
          </div>
        ))}
      </div>
    </div>
  );
};
